/**
 * The one place that offers to install plane: the browser's own dialog where there is one,
 * written instructions where there is not.
 *
 * - **Prompt.** Chromium fires `beforeinstallprompt` once the app is installable. Buffered by the
 *   inline script in `index.html` (src/lib/install/), it becomes an "Install" button: the native
 *   dialog opens on a click the user chose to make, the only way `prompt()` may be called at all.
 * - **Hint.** Safari never fires that event: on iOS the only way in is Share → "Add to Home
 *   Screen". `navigator.standalone` exists only on iOS Safari, `false` while browsing and `true`
 *   once launched from the home screen, so it answers "iOS Safari, not installed yet" without
 *   reading the user agent.
 *
 * "Not now" is remembered in `localStorage`; a refused native dialog is not: Chromium already
 * throttles the event on its own, and remembering the refusal too would silence the offer for good.
 */
import { LitElement, html } from 'lit';

import { INSTALL_AVAILABLE_EVENT, INSTALL_COMPLETED_EVENT, takeInstallPrompt } from '../../lib/install/index.js';
import { formStyles } from '../../styles/shared/forms.js';
import { planeInstallHintStyles } from '../../styles/components/plane-install-hint.js';

const DISMISSED_KEY = 'plane:install-hint-dismissed';

class PlaneInstallHint extends LitElement {
  static properties = {
    mode: { state: true },
  };

  static styles = [
    formStyles,
    planeInstallHintStyles,
  ];

  constructor() {
    super();
    /** @type {'none'|'prompt'|'hint'} */
    this.mode = 'none';
    this.onAvailable = () => {
      if (this.mode === 'none' && !isDismissed()) {
        this.mode = 'prompt';
      }
    };
    this.onInstalled = () => {
      this.mode = 'none';
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.mode = initialMode();
    document.addEventListener(INSTALL_AVAILABLE_EVENT, this.onAvailable);
    document.addEventListener(INSTALL_COMPLETED_EVENT, this.onInstalled);
  }

  disconnectedCallback() {
    document.removeEventListener(INSTALL_AVAILABLE_EVENT, this.onAvailable);
    document.removeEventListener(INSTALL_COMPLETED_EVENT, this.onInstalled);
    super.disconnectedCallback();
  }

  /** `prompt()` is single-use: the event is spent either way, so the band closes on both outcomes. */
  async install() {
    const deferredPrompt = takeInstallPrompt();
    this.mode = 'none';
    if (!deferredPrompt) {
      return;
    }
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch (error) {
      console.error('Unable to show the install prompt:', error);
    }
  }

  /** The explicit way out, and the only one that is remembered. */
  dismiss() {
    this.mode = 'none';
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // storage refused (private browsing): the band will come back next visit, nothing worse
    }
  }

  render() {
    this.hidden = this.mode === 'none';
    if (this.mode === 'prompt') {
      return html`
        <div class="band" role="status">
          <div class="hint">
            <p>Install plane: it opens in its own window, like an app.</p>
            <div class="actions">
              <button type="button" class="small primary" @click=${this.install}>Install</button>
              <button type="button" class="small" @click=${this.dismiss}>Not now</button>
            </div>
          </div>
        </div>
      `;
    }
    if (this.mode === 'hint') {
      return html`
        <div class="band" role="status">
          <div class="hint">
            <p>To add plane to your Home Screen: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.</p>
            <button type="button" class="small" @click=${this.dismiss}>Got it</button>
          </div>
        </div>
      `;
    }
    return null;
  }
}

function isDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function initialMode() {
  if (isDismissed()) {
    return 'none';
  }
  // The event may have fired before this element existed (that is why it is buffered): read the
  // buffer, do not wait for the event.
  if (takeInstallPrompt({ peek: true })) {
    return 'prompt';
  }
  return 'standalone' in navigator && navigator.standalone === false ? 'hint' : 'none';
}

customElements.define('plane-install-hint', PlaneInstallHint);
